import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  getAuth,
  updatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { db } from '../../config/firebase';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { MainNavbar } from '../../components/navigation/MainNavbar';

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();
  const auth = getAuth();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    email: '',
    address: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [initialUsername, setInitialUsername] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.uid) {
        setLoadingProfile(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setForm({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            username: (data.username || '').toLowerCase(),
            phoneNumber: data.phoneNumber || '',
            email: user.email || data.email || '',
            address: data.address || data.addressDescription || '',
          });
          setInitialUsername((data.username || '').toLowerCase());
        } else {
          setForm(prev => ({
            ...prev,
            email: user.email || '',
          }));
        }
      } catch (error) {
        console.error('Failed to load profile', error);
        setStatus({
          type: 'error',
          message: t('editProfile.loadError'),
        });
      } finally {
        setLoadingProfile(false);
      }
    };

    if (!initializing) {
      loadProfile();
    }
  }, [user?.uid, user?.email, initializing]);

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: field === 'username' ? value.toLowerCase() : value,
    }));
  };

  const canSubmit = useMemo(() => {
    return (
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.username.trim() &&
      form.email.trim()
    );
  }, [form]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!user?.uid || !canSubmit) return;

    if (newPassword || confirmPassword) {
      if (!currentPassword) {
        setStatus({
          type: 'error',
          message: t('editProfile.currentPasswordRequired'),
        });
        return;
      }
      if (newPassword !== confirmPassword) {
        setStatus({
          type: 'error',
          message: t('editProfile.passwordsMismatch'),
        });
        return;
      }
      if (newPassword.length < 6) {
        setStatus({
          type: 'error',
          message: t('editProfile.passwordTooShort'),
        });
        return;
      }
    }

    setSaving(true);
    setStatus({ type: '', message: '' });
    const username = form.username.trim().toLowerCase();
    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();

    try {
      if (username && username !== initialUsername) {
        await runTransaction(db, async transaction => {
          const usernamesRef = doc(db, 'usernames', username);
          const usernameSnap = await transaction.get(usernamesRef);

          if (usernameSnap.exists()) {
            const data = usernameSnap.data() || {};
            const takenByUid = data.uid && data.uid !== user.uid;
            const takenByEmail =
              !data.uid &&
              data.email &&
              data.email.toLowerCase() !== (user.email || '').toLowerCase();

            if (takenByUid || takenByEmail) {
              throw new Error('username_taken');
            }
          }

          transaction.set(
            usernamesRef,
            {
              userId: user.uid,
              uid: user.uid,
              email: (user.email || '').toLowerCase(),
              username,
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );

          if (initialUsername && initialUsername !== username) {
            transaction.delete(doc(db, 'usernames', initialUsername));
          }
        });
      }

      // Reauthenticate if password is being changed
      if (newPassword && currentPassword && auth.currentUser) {
        const credential = EmailAuthProvider.credential(
          user.email || '',
          currentPassword,
        );
        await reauthenticateWithCredential(auth.currentUser, credential);
      }

      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(
        userDocRef,
        {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phoneNumber: form.phoneNumber.trim(),
          username,
          displayName: fullName,
          address: form.address.trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: fullName,
        }).catch(() => {});
        if (newPassword) {
          await updatePassword(auth.currentUser, newPassword);
        }
      }

      // Update user's name in all their trips and requests
      try {
        const batch = writeBatch(db);
        let updateCount = 0;
        const maxBatchSize = 500; // Firestore batch limit

        // Update trips where user is the owner
        const tripsQuery = query(
          collection(db, 'trips'),
          where('createdBy', '==', user.uid),
        );
        const tripsSnapshot = await getDocs(tripsQuery);
        tripsSnapshot.docs.forEach(tripDoc => {
          if (updateCount < maxBatchSize) {
            const updateData = {
              createdByName: fullName,
              createdByEmail: user.email || '',
              createdByUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.createdByAddress = form.address.trim();
            }
            batch.update(tripDoc.ref, updateData);
            updateCount++;
          }
        });

        // Also check trips with ownerId field
        const tripsQuery2 = query(
          collection(db, 'trips'),
          where('ownerId', '==', user.uid),
        );
        const tripsSnapshot2 = await getDocs(tripsQuery2);
        tripsSnapshot2.docs.forEach(tripDoc => {
          if (
            updateCount < maxBatchSize &&
            !tripsSnapshot.docs.find(d => d.id === tripDoc.id)
          ) {
            const updateData = {
              createdByName: fullName,
              createdByEmail: user.email || '',
              createdByUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.createdByAddress = form.address.trim();
            }
            batch.update(tripDoc.ref, updateData);
            updateCount++;
          }
        });

        // Also check trips with userId field
        const tripsQuery3 = query(
          collection(db, 'trips'),
          where('userId', '==', user.uid),
        );
        const tripsSnapshot3 = await getDocs(tripsQuery3);
        tripsSnapshot3.docs.forEach(tripDoc => {
          if (
            updateCount < maxBatchSize &&
            !tripsSnapshot.docs.find(d => d.id === tripDoc.id) &&
            !tripsSnapshot2.docs.find(d => d.id === tripDoc.id)
          ) {
            const updateData = {
              createdByName: fullName,
              createdByEmail: user.email || '',
              createdByUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.createdByAddress = form.address.trim();
            }
            batch.update(tripDoc.ref, updateData);
            updateCount++;
          }
        });

        // Also check trips with createdByUid field
        const tripsQuery4 = query(
          collection(db, 'trips'),
          where('createdByUid', '==', user.uid),
        );
        const tripsSnapshot4 = await getDocs(tripsQuery4);
        tripsSnapshot4.docs.forEach(tripDoc => {
          if (
            updateCount < maxBatchSize &&
            !tripsSnapshot.docs.find(d => d.id === tripDoc.id) &&
            !tripsSnapshot2.docs.find(d => d.id === tripDoc.id) &&
            !tripsSnapshot3.docs.find(d => d.id === tripDoc.id)
          ) {
            const updateData = {
              createdByName: fullName,
              createdByEmail: user.email || '',
              createdByUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.createdByAddress = form.address.trim();
            }
            batch.update(tripDoc.ref, updateData);
            updateCount++;
          }
        });

        // Update requests where user is the requester
        const requesterQuery = query(
          collection(db, 'requests'),
          where('requesterId', '==', user.uid),
        );
        const requesterSnapshot = await getDocs(requesterQuery);
        requesterSnapshot.docs.forEach(requestDoc => {
          if (updateCount < maxBatchSize) {
            const updateData = {
              requesterName: fullName,
              requesterEmail: user.email || '',
              requesterUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.requesterAddress = form.address.trim();
            }
            batch.update(requestDoc.ref, updateData);
            updateCount++;
          }
        });

        // Update requests where user is the trip owner
        const ownerQuery = query(
          collection(db, 'requests'),
          where('tripOwnerId', '==', user.uid),
        );
        const ownerSnapshot = await getDocs(ownerQuery);
        ownerSnapshot.docs.forEach(requestDoc => {
          if (updateCount < maxBatchSize) {
            const updateData = {
              tripOwnerName: fullName,
              tripOwnerEmail: user.email || '',
              tripOwnerUsername: username,
              updatedAt: serverTimestamp(),
            };
            if (form.address.trim()) {
              updateData.tripOwnerAddress = form.address.trim();
            }
            batch.update(requestDoc.ref, updateData);
            updateCount++;
          }
        });

        // Commit batch if there are updates
        if (updateCount > 0) {
          await batch.commit();
          console.log(`Updated ${updateCount} documents with new name`);
        }
      } catch (updateError) {
        // Don't fail the entire update if batch update fails
        console.error('Error updating related documents:', updateError);
      }

      setStatus({
        type: 'success',
        message: t('editProfile.updateSuccess'),
      });
      setInitialUsername(username);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/profile'), 1200);
    } catch (error) {
      console.error('Profile update failed', error);
      if (error.message === 'username_taken') {
        setStatus({
          type: 'error',
          message: t('editProfile.usernameTaken'),
        });
      } else if (error?.code === 'auth/requires-recent-login') {
        setStatus({
          type: 'error',
          message: t('editProfile.reauthRequired'),
        });
      } else {
        setStatus({
          type: 'error',
          message: error.message || t('editProfile.updateErrorFallback'),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-3xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
              {t('editProfile.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('editProfile.loginRequired')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="rounded-2xl bg-black dark:bg-slate-700 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('incomingRequests.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="pt-28 pb-12 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.4em] text-gray-400 dark:text-slate-500">
            {t('profile.sectionLabel')}
          </p>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white mt-2">
            {t('editProfile.heroTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-3 max-w-2xl">
            {t('editProfile.heroSubtitle')}
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-white dark:bg-slate-900 shadow-xl border border-gray-100 dark:border-slate-800 p-8">
          {status.message && (
            <div
              className={`mb-6 rounded-2xl border px-4 py-3 text-sm ${
                status.type === 'error'
                  ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  : 'border-green-200 dark:border-green-900/50 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              }`}
            >
              {status.message}
            </div>
          )}

          {loadingProfile ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div
                  key={idx}
                  className="h-12 w-full animate-pulse rounded-2xl bg-gray-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.firstName')}
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={e => handleChange('firstName', e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.firstNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.lastName')}
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={e => handleChange('lastName', e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.lastNamePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                  {t('editProfile.username')}
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={e => handleChange('username', e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                  placeholder={t('editProfile.usernamePlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                  {t('editProfile.usernameHelp')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.email')}
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    disabled
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-3 text-sm text-gray-500 dark:text-slate-400"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.phone')}
                  </label>
                  <input
                    type="tel"
                    value={form.phoneNumber}
                    onChange={e => handleChange('phoneNumber', e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.phonePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                  {t('editProfile.address')}
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={e => handleChange('address', e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                  placeholder={t('editProfile.addressPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                  {t('editProfile.addressHelp')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.currentPasswordPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.newPasswordPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-slate-300">
                    {t('editProfile.confirmNewPassword')}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-3 text-sm focus:border-yellow-400 dark:focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 dark:focus:ring-yellow-900/30"
                    placeholder={t('editProfile.confirmNewPasswordPlaceholder')}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4">
                <button
                  type="submit"
                  disabled={!canSubmit || saving}
                  className="flex items-center rounded-2xl bg-black dark:bg-slate-700 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving
                    ? t('editProfile.saving')
                    : t('editProfile.saveChanges')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="rounded-2xl border border-gray-200 dark:border-slate-700 px-5 py-3 text-sm font-semibold text-gray-600 dark:text-slate-300 hover:border-yellow-300 dark:hover:border-yellow-500 hover:text-gray-900 dark:hover:text-white"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
